import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { fetchProduct, fetchBundles, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { IMAGES } from "../data/site";
import { ChevronRight, Ruler } from "lucide-react";
import { toast } from "sonner";
import Reviews, { Stars } from "../components/Reviews";
import GripSelector from "../components/GripSelector";
import ProductGallery from "../components/product/ProductGallery";
import PackSizeSelector from "../components/product/PackSizeSelector";
import SizeGuideDialog from "../components/product/SizeGuideDialog";
import BundleUpsell from "../components/product/BundleUpsell";
import RelatedColourways from "../components/product/RelatedColourways";
import useSeo from "../hooks/useSeo";
import { BACKEND_URL } from "../lib/api";

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem, setOpen } = useCart();

  const [product, setProduct] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [selectedColour, setSelectedColour] = useState(null);
  const [size, setSize] = useState(null);
  const [grip, setGrip] = useState(null);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  // Pre-select the 3-pair Matchday Rotation pack (9% saving).
  const [packSlug, setPackSlug] = useState("matchday-rotation");

  useEffect(() => {
    fetchProduct().then((p) => {
      setProduct(p);
      const params = new URLSearchParams(location.search);
      const c = params.get("c");
      const g = params.get("g");
      const pack = params.get("pack");
      const initial = p.colourways.find((cw) => cw.slug === c) || p.colourways[0];
      setSelectedColour(initial);
      setSize(p.sizes[1] || p.sizes[0]);
      const initialGrip =
        (p.grips || []).find((gr) => gr.slug === g)?.slug ||
        p.default_grip ||
        p.grips?.[0]?.slug;
      setGrip(initialGrip);
      // If user came from BundlesSection "Build pack" CTA, pre-select that pack.
      if (pack) setPackSlug(pack);
    });
    fetchBundles()
      .then(setBundles)
      .catch((err) => console.warn("[product] failed to load bundles", err));
  }, [slug, location.search]);

  const gallery = useMemo(() => {
    if (!product || !selectedColour) return [];
    const gripImage = selectedColour.grip_images?.[grip];
    const primary = gripImage || selectedColour.image || product.image;
    return [primary, IMAGES.productFeatures, IMAGES.matchday, IMAGES.matchdaySystem].filter(Boolean);
  }, [product, selectedColour, grip]);

  useSeo({
    title: product
      ? `${product.name} · ${selectedColour?.name || "12 Colourways"} · ${formatPrice(product.price)}`
      : "The Pritch Grip Sock",
    description: product
      ? `${product.name} — ${product.description.split(".")[0]}. ${product.colourways.length} colourways from ${formatPrice(product.price)}.`
      : undefined,
    path: `/product/pritch-grip-sock${selectedColour ? `?c=${selectedColour.slug}` : ""}`,
    image: selectedColour?.image
      ? `${BACKEND_URL}${selectedColour.image}`
      : undefined,
    jsonLdId: "product-jsonld",
    jsonLd:
      product && selectedColour
        ? {
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            description: product.description,
            sku: `PRITCH-${selectedColour.slug.toUpperCase()}`,
            brand: { "@type": "Brand", name: "Pritch" },
            category: "Football Grip Socks",
            image: selectedColour.image
              ? `${BACKEND_URL}${selectedColour.image}`
              : "https://customer-assets.emergentagent.com/job_grip-pitch/artifacts/wmpezwr4_5.png",
            color: selectedColour.name,
            offers: {
              "@type": "Offer",
              priceCurrency: "GBP",
              price: product.price.toFixed(2),
              availability: "https://schema.org/InStock",
              itemCondition: "https://schema.org/NewCondition",
              url: `https://pritch.fc/product/pritch-grip-sock?c=${selectedColour.slug}`,
            },
            aggregateRating:
              product.review_count > 0
                ? {
                    "@type": "AggregateRating",
                    ratingValue: product.rating.toFixed(2),
                    reviewCount: product.review_count,
                    bestRating: 5,
                    worstRating: 1,
                  }
                : undefined,
          }
        : undefined,
  });

  if (!product || !selectedColour) {
    return (
      <div className="container-x py-32 text-center text-mute" data-testid="product-loading">
        Loading…
      </div>
    );
  }

  const onAdd = () => {
    const pack = bundles.find((b) => b.slug === packSlug);
    // Single pair → add the sock with selected colour/size/grip.
    // 3 / 5 pair bundle → add the bundle line item (priced server-side).
    if (!pack || pack.pairs === 1) {
      const selectedGrip = (product.grips || []).find((g) => g.slug === grip);
      addItem({
        type: "sock",
        slug: product.slug,
        name: product.name,
        colourway: selectedColour.slug,
        colourwayLabel: selectedColour.name,
        swatch: selectedColour.hex,
        size,
        grip,
        gripLabel: selectedGrip?.name,
        price: product.price,
        image: product.image,
      });
      toast.success(`${selectedColour.name} added to cart`);
      return;
    }
    addItem({
      type: "bundle",
      slug: pack.slug,
      name: pack.name,
      colourwayLabel: `${pack.pairs} pairs · ${selectedColour.name} starter`,
      swatch: selectedColour.hex,
      price: pack.price,
      image: pack.image,
    });
    toast.success(`${pack.name} added · save ${formatPrice(pack.saving)}`);
  };

  const onAddBundle = (bundle) => {
    addItem({
      type: "bundle",
      slug: bundle.slug,
      name: bundle.name,
      colourwayLabel: `${bundle.pairs} pair${bundle.pairs > 1 ? "s" : ""}`,
      swatch: "#F5F4F0",
      price: bundle.price,
      image: bundle.image,
    });
    toast.success(`${bundle.name} added`);
  };

  // When the user picks a specific colour swatch they're signalling "I want this
  // colour". A multi-pair bundle is a generic mixed pack — keeping it selected
  // would mean their Add to cart adds a bundle with just a cosmetic colour
  // label, not the colour they clicked. Drop back to the 1-pair pack so the
  // colour choice has real meaning. Customers who want a multi-pair pack still
  // click the 3- or 5-pair tile explicitly.
  const onPickColour = (c) => {
    setSelectedColour(c);
    if (packSlug !== "starter-pair") setPackSlug("starter-pair");
  };

  const onPickRelated = (c) => {
    onPickColour(c);
    setActiveImage(0);
    navigate(`/product/pritch-grip-sock?c=${c.slug}`);
  };

  const packPrice = bundles.find((b) => b.slug === packSlug)?.price ?? product.price;

  return (
    <div data-testid="product-page" className="bg-chalk">
      {/* Breadcrumb */}
      <div className="container-x py-4 text-[11px] tracking-[0.18em] uppercase text-mute flex items-center gap-2">
        <button onClick={() => navigate("/")} className="hover:text-ink">Home</button>
        <ChevronRight size={12} />
        <button onClick={() => navigate("/shop")} className="hover:text-ink">Shop</button>
        <ChevronRight size={12} />
        <span className="text-ink">{product.name}</span>
      </div>

      <section className="container-x grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 pb-16">
        <ProductGallery
          gallery={gallery}
          selectedColour={selectedColour}
          activeImage={activeImage}
          setActiveImage={setActiveImage}
        />

        {/* Right column */}
        <div className="lg:col-span-5">
          <p className="overline mb-3">Series 01</p>
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tightest font-bold leading-[0.95]">
            {product.name}
          </h1>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <p className="mono text-base">{formatPrice(product.price)}</p>
            {product.review_count > 0 && (
              <div className="flex items-center gap-2" data-testid="product-rating">
                <Stars value={product.rating} size={13} />
                <span className="mono text-xs text-mute">
                  {product.rating?.toFixed(1)} · {product.review_count} review
                  {product.review_count === 1 ? "" : "s"}
                </span>
              </div>
            )}
          </div>
          <p className="mt-6 text-mute leading-relaxed text-sm">{product.description}</p>

          {/* Colour selector */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Colourway · {selectedColour.name}</p>
              <span className="mono text-xs text-mute">{product.colourways.length} options</span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="colour-selector">
              {product.colourways.map((c) => (
                <button
                  type="button"
                  key={c.slug}
                  onClick={() => onPickColour(c)}
                  className={`w-9 h-9 border ${
                    selectedColour.slug === c.slug
                      ? "ring-2 ring-ink ring-offset-2 ring-offset-chalk border-line"
                      : "border-line hover:scale-110"
                  } transition-transform`}
                  style={{ backgroundColor: c.hex }}
                  title={c.name}
                  aria-label={c.name}
                  data-testid={`colour-swatch-${c.slug}`}
                />
              ))}
            </div>
          </div>

          {/* Size selector */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Size</p>
              <button
                type="button"
                onClick={() => setSizeGuideOpen(true)}
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-mute hover:text-ink"
                data-testid="open-size-guide"
              >
                <Ruler size={12} /> Size guide
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSize(s)}
                  className={`py-3 text-[12px] tracking-[0.16em] uppercase font-bold border transition-colors ${
                    size === s
                      ? "bg-ink text-white border-ink"
                      : "bg-white text-ink border-line hover:border-ink"
                  }`}
                  data-testid={`size-${s.replace(/\s+/g, "-")}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Grip selector */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <p className="overline">
                Grip · {(product.grips || []).find((g) => g.slug === grip)?.name || "Pritch Grid"}
              </p>
              <span className="mono text-xs text-mute">{product.grips?.length || 8} patterns</span>
            </div>
            <GripSelector
              grips={product.grips || []}
              value={grip}
              onChange={setGrip}
              testIdPrefix="grip"
            />
          </div>

          <PackSizeSelector bundles={bundles} packSlug={packSlug} onChange={setPackSlug} />

          <button
            type="button"
            onClick={onAdd}
            className="btn-primary w-full mt-8"
            data-testid="add-to-cart-button"
          >
            Add to cart · {formatPrice(packPrice)}
          </button>
          <button
            type="button"
            onClick={() => {
              onAdd();
              setOpen(true);
            }}
            className="btn-secondary w-full mt-3"
            data-testid="buy-now-button"
          >
            Buy now
          </button>

          {/* Features accordion */}
          <Accordion type="multiple" defaultValue={["features"]} className="mt-10 border-t border-line">
            <AccordionItem value="features" className="border-b border-line">
              <AccordionTrigger className="py-4 text-[12px] tracking-[0.18em] uppercase font-bold hover:no-underline">
                Key features
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <ul className="space-y-2 text-sm text-mute">
                  {product.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="text-ink mono">·</span> {f}
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="care" className="border-b border-line">
              <AccordionTrigger className="py-4 text-[12px] tracking-[0.18em] uppercase font-bold hover:no-underline">
                Washing instructions
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <ul className="space-y-2 text-sm text-mute">
                  {product.care.map((c) => (
                    <li key={c}>· {c}</li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="delivery" className="border-b border-line">
              <AccordionTrigger className="py-4 text-[12px] tracking-[0.18em] uppercase font-bold hover:no-underline">
                Delivery & returns
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm text-mute leading-relaxed">
                UK delivery 2–4 working days · Free over £50. EU 5–8 days. Returns accepted within
                30 days of receipt, in original condition.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <BundleUpsell bundles={bundles} onAdd={onAddBundle} />
      <RelatedColourways product={product} selectedColour={selectedColour} onPick={onPickRelated} />

      {/* Reviews */}
      <Reviews productSlug="pritch-grip-sock" />

      <SizeGuideDialog open={sizeGuideOpen} onOpenChange={setSizeGuideOpen} />

      {/* Sticky mobile add-to-cart */}
      <div className="lg:hidden sticky bottom-0 z-30 bg-white border-t border-line p-3 flex items-center gap-3">
        <div>
          <p className="mono text-xs text-mute">Pritch · {selectedColour.name}</p>
          <p className="mono text-sm">{formatPrice(product.price)}</p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="btn-primary flex-1"
          data-testid="sticky-add-to-cart"
        >
          Add to cart
        </button>
      </div>
    </div>
  );
};

export default ProductPage;
